if (!customElements.get('libertas-hero-slideshow')) {
  class LibertasHeroSlideshow extends HTMLElement {
    connectedCallback() {
      if (this.dataset.heroReady === 'true') return;
      this.dataset.heroReady = 'true';
      this.slides = [...this.querySelectorAll('[data-hero-slide]')];
      this.dots = [...this.querySelectorAll('[data-hero-dot]')];
      this.arrows = this.querySelector('[data-hero-arrows]');
      this.navigation = this.querySelector('[data-hero-navigation]');
      this.previousButton = this.querySelector('[data-hero-previous]');
      this.nextButton = this.querySelector('[data-hero-next]');
      this.pauseButton = this.querySelector('[data-hero-pause]');
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.autoplay = this.dataset.autoplay === 'true';
      this.delay = Math.max(1, Number(this.dataset.changeEvery) || 5) * 1000;
      this.manualPaused = false;
      this.hoverPaused = false;
      this.focusPaused = false;
      this.touchPaused = false;
      this.touchStartX = null;
      this.touchStartY = null;
      this.abortController = new AbortController();
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobilePreference = window.matchMedia('(max-width: 749px)');
      const { signal } = this.abortController;

      this.previousButton?.addEventListener('click', () => this.showSlide(this.index - 1), { signal });
      this.nextButton?.addEventListener('click', () => this.showSlide(this.index + 1), { signal });
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.showSlide(Number(dot.dataset.heroDot)), { signal });
      });
      this.pauseButton?.addEventListener('click', () => {
        this.manualPaused = !this.manualPaused;
        this.updatePauseButton();
        this.schedule();
      }, { signal });
      this.addEventListener('pointerenter', () => {
        this.hoverPaused = true;
        this.schedule();
      }, { signal });
      this.addEventListener('pointerleave', () => {
        this.hoverPaused = false;
        this.schedule();
      }, { signal });
      this.addEventListener('focusin', () => {
        this.focusPaused = true;
        this.schedule();
      }, { signal });
      this.addEventListener('focusout', (event) => {
        if (this.contains(event.relatedTarget)) return;
        this.focusPaused = false;
        this.schedule();
      }, { signal });
      this.addEventListener('touchstart', (event) => {
        const touch = event.touches[0];
        if (!touch) return;
        this.touchPaused = true;
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
        this.schedule();
      }, { signal, passive: true });
      this.addEventListener('touchend', (event) => {
        const touch = event.changedTouches[0];
        const deltaX = touch && this.touchStartX !== null ? touch.clientX - this.touchStartX : 0;
        const deltaY = touch && this.touchStartY !== null ? touch.clientY - this.touchStartY : 0;
        this.touchPaused = false;
        this.touchStartX = null;
        this.touchStartY = null;
        if (Math.abs(deltaX) > 45 && Math.abs(deltaX) > Math.abs(deltaY)) {
          this.showSlide(this.index + (deltaX < 0 ? 1 : -1));
          return;
        }
        this.schedule();
      }, { signal, passive: true });
      this.addEventListener('touchcancel', () => {
        this.touchPaused = false;
        this.touchStartX = null;
        this.touchStartY = null;
        this.schedule();
      }, { signal, passive: true });
      document.addEventListener('visibilitychange', () => {
        this.syncVideos();
        this.schedule();
      }, { signal });
      this.motionPreference.addEventListener('change', () => this.schedule(), { signal });
      this.mobilePreference.addEventListener('change', () => this.showSlide(this.index, false), { signal });

      const hasMultipleSlides = this.slides.length > 1;
      if (this.arrows) this.arrows.hidden = !hasMultipleSlides;
      if (this.navigation) this.navigation.hidden = !hasMultipleSlides;
      this.showSlide(this.index, false);
      this.updatePauseButton();
      this.schedule();
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      this.abortController?.abort();
      this.querySelectorAll('video').forEach((video) => video.pause());
      delete this.dataset.heroReady;
    }

    showSlide(targetIndex, restart = true) {
      if (this.slides.length === 0) return;
      this.index = (targetIndex + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });
      this.dots.forEach((dot, index) => {
        const active = index === this.index;
        dot.classList.toggle('is-active', active);
        if (active) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      const activeSlide = this.slides[this.index];
      this.style.setProperty('--hero-aspect-ratio', activeSlide.dataset.desktopRatio || '2.57');
      this.style.setProperty('--hero-mobile-aspect-ratio', activeSlide.dataset.mobileRatio || activeSlide.dataset.desktopRatio || '2.57');
      this.classList.toggle('has-mobile-media', activeSlide.dataset.hasMobileMedia === 'true');
      this.syncVideos();
      if (restart) this.schedule();
    }

    syncVideos() {
      this.querySelectorAll('video').forEach((video) => video.pause());
      if (document.hidden) return;
      const activeSlide = this.slides[this.index];
      window.requestAnimationFrame(() => {
        activeSlide?.querySelectorAll('video').forEach((video) => {
          if (video.getClientRects().length === 0) return;
          video.muted = true;
          video.play().catch(() => {});
        });
      });
    }

    schedule() {
      window.clearTimeout(this.timer);
      if (
        !this.autoplay
        || this.slides.length < 2
        || this.manualPaused
        || this.hoverPaused
        || this.focusPaused
        || this.touchPaused
        || document.hidden
        || this.motionPreference.matches
      ) {
        return;
      }
      this.timer = window.setTimeout(() => this.showSlide(this.index + 1), this.delay);
    }

    updatePauseButton() {
      if (!this.pauseButton) return;
      this.pauseButton.classList.toggle('is-paused', this.manualPaused);
      this.pauseButton.setAttribute('aria-pressed', String(this.manualPaused));
      this.pauseButton.setAttribute('aria-label', this.manualPaused ? 'Reanudar slideshow' : 'Pausar slideshow');
    }
  }

  customElements.define('libertas-hero-slideshow', LibertasHeroSlideshow);
}

if (!window.libertasHeroBlockSelectReady) {
  window.libertasHeroBlockSelectReady = true;
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-hero-slide]');
    const slideshow = slide?.closest('libertas-hero-slideshow');
    if (!slide || !slideshow) return;
    slideshow.manualPaused = true;
    slideshow.updatePauseButton();
    slideshow.showSlide(slideshow.slides.indexOf(slide));
  });
}
